import { useEffect, useRef, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth-provider/useAuth";
import { useGetMe } from "@/queries/me";
import { getWebSocketUrl } from "@/utils/reverb-config";
import { NOTIFICATIONS_QUERY_KEY } from "@/queries/notifications";
import axios from "@/utils/axios";
import type {
  ConnectionStatus,
  ReverbMessage,
  UseReverbOptions,
} from "./types";
import {
  state,
  addTimer,
  clearTimer,
  notifyStatus,
  getStatus,
} from "./singleton";

export const useReverb = (options: UseReverbOptions = {}) => {
  const {
    enabled = true,
    onNotification,
    onFlowDuplicationCompleted,
    autoReconnect = true,
    maxReconnectAttempts = 5,
  } = options;

  const { authenticated, keycloak } = useAuth();
  const { data: meData } = useGetMe();
  const queryClient = useQueryClient();

  const userId = meData?.data?.id;
  const userIdRef = useRef(userId);
  const onNotificationRef = useRef(onNotification);
  const onFlowDuplicationCompletedRef = useRef(onFlowDuplicationCompleted);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    onFlowDuplicationCompletedRef.current = onFlowDuplicationCompleted;
  }, [onFlowDuplicationCompleted]);

  const [status, setStatus] = useState<ConnectionStatus>(getStatus());

  useEffect(() => {
    const listener = (nextStatus: ConnectionStatus) => {
      setStatus(nextStatus);
    };

    state._statusListeners.add(listener);

    setStatus(getStatus());

    return () => {
      state._statusListeners.delete(listener);
    };
  }, []);

  const subscribeToChannel = useCallback(
    async (
      channelName: string,
      socketId: string,
      socket: WebSocket,
      attempts = 0,
    ) => {
      if (state._ws !== socket || socket.readyState !== WebSocket.OPEN) {
        if (attempts < 3 && state._ws === socket) {
          addTimer(() => {
            void subscribeToChannel(
              channelName,
              socketId,
              socket,
              attempts + 1,
            );
          }, 1000);
        }

        return;
      }

      try {
        const response = await axios.post(
          "/broadcasting/auth",
          new URLSearchParams({
            channel_name: channelName,
            socket_id: socketId,
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          },
        );

        if (state._ws !== socket || socket.readyState !== WebSocket.OPEN) {
          return;
        }

        const { auth } = response.data;

        socket.send(
          JSON.stringify({
            event: "pusher:subscribe",
            data: JSON.stringify({
              channel: channelName,
              auth,
            }),
          }),
        );

        console.log(`[Reverb] Subscribed to channel: ${channelName}`);
      } catch (error) {
        console.error(`[Reverb] Subscription error for ${channelName}:`, error);

        if (attempts < 3 && state._ws === socket) {
          console.log(
            `[Reverb] Retrying subscription for ${channelName} ` +
              `(attempt ${attempts + 1}/3)...`,
          );

          addTimer(() => {
            void subscribeToChannel(
              channelName,
              socketId,
              socket,
              attempts + 1,
            );
          }, 1000);
        }
      }
    },
    [],
  );

  const cleanup = useCallback(() => {
    if (state._reconnectTimeout) {
      clearTimer(state._reconnectTimeout);
      state._reconnectTimeout = null;
    }
    if (state._pingInterval) {
      clearInterval(state._pingInterval);
      state._pingInterval = null;
    }
    if (state._ws) {
      const ws = state._ws;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      if (
        ws.readyState !== WebSocket.CLOSED &&
        ws.readyState !== WebSocket.CLOSING
      ) {
        ws.close(1000, "Client cleanup");
      }

      state._ws = null;
    }

    state._connecting = false;
  }, []);

  const disconnect = useCallback(() => {
    cleanup();
    state._reconnectAttempts = 0;
    notifyStatus("disconnected");
  }, [cleanup]);

  const scheduleReconnect = useCallback(() => {
    if (!autoReconnect || !enabled) {
      return;
    }
    if (state._activeCount === 0) {
      return;
    }
    if (state._reconnectAttempts >= maxReconnectAttempts) {
      console.error("[Reverb] Max reconnection attempts reached");

      notifyStatus("error");
      return;
    }
    if (state._reconnectTimeout) {
      return;
    }
    state._reconnectAttempts += 1;
    const delay = Math.min(
      1000 * Math.pow(2, state._reconnectAttempts - 1),
      30000,
    );
    console.log(
      `[Reverb] Reconnecting in ${delay}ms ` +
        `(attempt ${state._reconnectAttempts}/${maxReconnectAttempts})`,
    );
    state._reconnectTimeout = addTimer(() => {
      state._reconnectTimeout = null;
      if (state._activeCount === 0) {
        return;
      }
      if (!enabled || !autoReconnect) {
        return;
      }
      if (userIdRef.current) {
        const reconnect = state._connectFns.values().next().value;
        reconnect?.();
      }
    }, delay);
  }, [autoReconnect, enabled, maxReconnectAttempts]);

  const connect = useCallback(() => {
    const currentUserId = userIdRef.current;
    if (!authenticated || !currentUserId) {
      console.log(
        "[Reverb] Not authenticated or no user ID, skipping connection",
      );
      return;
    }
    if (state._connecting) {
      console.log("[Reverb] Connection already in progress");
      return;
    }
    if (state._ws?.readyState === WebSocket.OPEN) {
      console.log("[Reverb] Already connected");
      return;
    }
    if (state._ws?.readyState === WebSocket.CONNECTING) {
      console.log("[Reverb] Already connecting");
      return;
    }
    cleanup();
    state._connecting = true;
    notifyStatus("connecting");
    const wsUrl = getWebSocketUrl();
    console.log("[Reverb] Connecting to:", wsUrl);
    const ws = new WebSocket(wsUrl);
    state._ws = ws;
    ws.onopen = () => {
      console.log(
        "[Reverb] WebSocket connection opened, " +
          "waiting for server handshake...",
      );
    };
    ws.onmessage = (event: MessageEvent) => {
      if (state._ws !== ws) {
        return;
      }
      try {
        const message: ReverbMessage = JSON.parse(event.data);

        console.log("[Reverb] Received message:", message);

        switch (message.event) {
          case "auth.logout":
          case ".auth.logout": {
            console.log("[Reverb] Received auth.logout event, logging out");

            keycloak?.logout();
            break;
          }

          case "pusher:connection_established": {
            const data = JSON.parse(message.data);
            const socketId = data.socket_id;

            state._connecting = false;
            state._reconnectAttempts = 0;

            notifyStatus("connected");

            console.log("[Reverb] Connected with socket_id:", socketId);

            for (const activeUserId of state._activeUserIds) {
              const channelName = `private-notifications.${activeUserId}`;

              void subscribeToChannel(channelName, socketId, ws);
            }

            break;
          }

          case "pusher_internal:subscription_succeeded":
            console.log(
              "[Reverb] Successfully subscribed to:",
              message.channel,
            );
            break;

          case "pusher:error": {
            const errorData = JSON.parse(message.data);

            console.error("[Reverb] Server error:", errorData);

            notifyStatus("error");
            break;
          }

          case "pusher:pong":
            break;

          case "notification.created":
          case ".notification.created": {
            const notification = JSON.parse(message.data);

            console.log("[Reverb] New notification received:", notification);

            queryClient.invalidateQueries({
              queryKey: [NOTIFICATIONS_QUERY_KEY],
            });

            onNotificationRef.current?.(notification);

            break;
          }

          case "flow.duplication.completed":
          case ".flow.duplication.completed": {
            const payload = JSON.parse(message.data);

            console.log("[Reverb] Flow duplication completed:", payload);

            queryClient.invalidateQueries({
              queryKey: ["flows"],
            });

            queryClient.invalidateQueries({
              queryKey: ["main-flows"],
            });

            onFlowDuplicationCompletedRef.current?.(payload);

            break;
          }

          default:
            console.log("[Reverb] Unhandled event:", message.event, message);
        }
      } catch (error) {
        console.error("[Reverb] Failed to parse message:", error, event.data);
      }
    };

    ws.onerror = (error) => {
      if (state._ws !== ws) {
        return;
      }
      console.error("[Reverb] WebSocket error:", error);
      state._connecting = false;
      notifyStatus("error");
    };

    ws.onclose = (event) => {
      if (state._ws !== ws) {
        return;
      }
      console.log("[Reverb] WebSocket closed:", event.code, event.reason);
      state._connecting = false;
      if (state._pingInterval) {
        clearInterval(state._pingInterval);
        state._pingInterval = null;
      }
      state._ws = null;
      notifyStatus("disconnected");
      if (event.code !== 1000) {
        scheduleReconnect();
      }
    };
    state._pingInterval = setInterval(() => {
      if (state._ws === ws && ws.readyState === WebSocket.OPEN) {
        ws.send(
          JSON.stringify({
            event: "pusher:ping",
            data: {},
          }),
        );
      }
    }, 30000);
  }, [
    authenticated,
    cleanup,
    keycloak,
    queryClient,
    scheduleReconnect,
    subscribeToChannel,
  ]);

  useEffect(() => {
    state._connectFns.add(connect);
    return () => {
      state._connectFns.delete(connect);
    };
  }, [connect]);

  useEffect(() => {
    if (!enabled || !authenticated || !userId) {
      return;
    }
    let active = true;
    let consumed = false;
    const timeoutId = addTimer(() => {
      if (!active || !enabled || !authenticated || !userId) {
        return;
      }
      consumed = true;
      state._activeCount++;
      state._activeUserIds.add(String(userId));
      if (
        state._activeCount === 1 &&
        state._ws?.readyState !== WebSocket.OPEN &&
        !state._connecting
      ) {
        connect();
      }
    }, 100);

    return () => {
      active = false;
      clearTimer(timeoutId);
      if (!consumed) {
        return;
      }
      const normalizedUserId = String(userId);
      state._activeUserIds.delete(normalizedUserId);
      if (state._activeCount > 0) {
        state._activeCount--;
      }
      if (state._activeCount === 0) {
        disconnect();
      }
    };
  }, [enabled, authenticated, userId, connect, disconnect]);

  return {
    status,
    isConnected: status === "connected",
    connect,
    disconnect,
  };
};
